import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Edit3, Plus, Search } from "lucide-react";
import Select from "react-select";
import {
  useFetchAppointmentTypeCriteriaMutation,
  useCreateAppointmentTypeCriteriaMutation,
  useUpdateAppointmentTypeCriteriaMutation,
} from "../../../../features/criterion/criterionApiSlice";
import { selectUser } from "../../../../features/auth/authSlice";
import { useSelector } from "react-redux";

const normalizeVisitTypes = (list, reference = []) =>
  (list || []).map((vt) => {
    const refName = reference.find((r) => r.id === (vt?.id ?? +vt ?? vt))
      ?.name;
    if (typeof vt === "string") {
      return { id: vt, name: refName || vt };
    }
    return {
      id: vt.id,
      name: vt.name || refName || vt.id,
    };
  });

const AppointmentTypeCriteria = ({ isWelcomePage }) => {
  const user = useSelector(selectUser);
  const [
    fetchAppointmentTypeCriteria,
    { isLoading: isFetchingAppointmentTypeCriteria },
  ] = useFetchAppointmentTypeCriteriaMutation();
  const [
    createAppointmentTypeCriteria,
    { isLoading: isCreatingAppointmentTypeCriteria },
  ] = useCreateAppointmentTypeCriteriaMutation();
  const [
    updateAppointmentTypeCriteria,
    { isLoading: isUpdatingAppointmentTypeCriteria },
  ] = useUpdateAppointmentTypeCriteriaMutation();
  const [criteria, setCriteria] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [carePrograms, setCarePrograms] = useState([]);
  const [payers, setPayers] = useState([]);
  const [lobs, setLobs] = useState([]);
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedPayers, setSelectedPayers] = useState([]);
  const [selectedLobs, setSelectedLobs] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedVisitTypes, setSelectedVisitTypes] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formValues, setFormValues] = useState({
    id: "",
    payerId: "",
    payerName: "",
    lobId: "",
    lobName: "",
    programId: "",
    programName: "",
    state: "",
    visitTypes: [],
  });
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const isSaving =
    isCreatingAppointmentTypeCriteria || isUpdatingAppointmentTypeCriteria;
  const idCounterRef = useRef(criteria.length + 1);

  const extractCriteria = (res) =>
    (res?.data?.criteria ?? res?.result ?? res ?? []).map((c) => ({
      id: c.id,
      payerId: c.payer?.id ?? "",
      payerName: c.payer?.name ?? "",
      lobId: c.lob?.id ?? "",
      lobName: c.lob?.name ?? "",
      programId: c.care_program?.id ?? "",
      programName: c.care_program?.name ?? "",
      state: c.state ?? "",
      visitTypes: normalizeVisitTypes(
        c.appointment_type_ids ?? [],
        c.appointment_type_ids ?? []
      ),
    }));

  const extractVisitTypes = (res) =>
    res?.data?.appointmentTypes ?? res?.result ?? res ?? [];

  const extractCarePrograms = (res) =>
    res?.data?.carePrograms ?? res?.result ?? res ?? [];

  const extractPayers = (res) => res?.data?.payers ?? res?.result ?? res ?? [];

  const extractLobs = (res) => res?.data?.lobs ?? res?.result ?? res ?? [];

  const extractStates = (res) => res?.data?.states ?? res?.result ?? res ?? [];

  const loadCriteria = useCallback(async () => {
    try {
      const res = await fetchAppointmentTypeCriteria().unwrap();
      setCriteria(extractCriteria(res));
      setVisitTypes(extractVisitTypes(res));
      setCarePrograms(extractCarePrograms(res));
      setPayers(extractPayers(res));
      setLobs(extractLobs(res));
      setStates(extractStates(res));
      setPageError("");
    } catch (err) {
      setPageError(err?.data?.message || "Failed to load criteria.");
      setCriteria([]);
      setVisitTypes([]);
      setCarePrograms([]);
      setPayers([]);
      setLobs([]);
      setStates([]);
    }
  }, [fetchAppointmentTypeCriteria]);

  useEffect(() => {
    loadCriteria();
  }, [loadCriteria]);

  const visitTypeSelectOptions = useMemo(
    () => [
      { label: "Select / Deselect All", value: "__all" },
      ...visitTypes.map((opt) => ({
        label: opt.name,
        value: opt.id,
      })),
    ],
    [visitTypes]
  );

  const filteredCriteria = useMemo(() => {
    const term = search.trim().toLowerCase();
    return criteria.filter((c) => {
      const normalizedVts = normalizeVisitTypes(c.visitTypes, visitTypes);
      const selectedVtIds = selectedVisitTypes.map((id) => String(id));
      const matchesSearch =
        !term ||
        c.payerName.toLowerCase().includes(term) ||
        c.lobName.toLowerCase().includes(term) ||
        c.programName.toLowerCase().includes(term) ||
        c.state.toLowerCase().includes(term) ||
        normalizedVts
          .map((vt) => vt.name)
          .join(", ")
          .toLowerCase()
          .includes(term);

      const matchesStates =
        selectedStates.length === 0 || selectedStates.includes(c.state);
      const matchesPayers =
        selectedPayers.length === 0 || selectedPayers.includes(c.payerId);
      const matchesLobs =
        selectedLobs.length === 0 || selectedLobs.includes(c.lobId);
      const matchesPrograms =
        selectedPrograms.length === 0 || selectedPrograms.includes(c.programId);
      const matchesVisitTypes =
        selectedVisitTypes.length === 0 ||
        normalizedVts.some((vt) => selectedVtIds.includes(String(vt.id)));

      return (
        matchesSearch &&
        matchesStates &&
        matchesPayers &&
        matchesLobs &&
        matchesPrograms &&
        matchesVisitTypes
      );
    });
  }, [
    criteria,
    search,
    selectedStates,
    selectedPayers,
    selectedLobs,
    selectedPrograms,
    selectedVisitTypes,
  ]);

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormValues({
      payerId: "",
      lobId: "",
      programId: "",
      state: "",
      visitTypes: [],
    });
    setError("");
  };

  const openAdd = () => {
    setEditing(null);
    setFormValues({
      payerId: "",
      lobId: "",
      programId: "",
      state: "",
      visitTypes: [],
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setFormValues({
      id: item.id,
      payerName: item.payerName,
      lobName: item.lobName,
      programName: item.programName,
      lobId: item.lobId,
      programId: item.programId,
      payerId: item.payerId,
      state: item.state,
      visitTypes: normalizeVisitTypes(item.visitTypes, visitTypes),
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { payerId, lobId, programId, state, visitTypes } = formValues;
    if (!payerId || !lobId || !programId || !state) {
      setError("All fields are required.");
      return;
    }
    const visitTypesArray = Array.isArray(visitTypes) ? visitTypes : [];
    if (visitTypesArray.length === 0) {
      setError("At least one visit type is required.");
      return;
    }

    const payload = {
      payer_id: payerId,
      lob_id: lobId,
      care_program_id: programId,
      state,
      appointment_type_ids: visitTypesArray.map((vt) => vt.id ?? vt),
    };

    try {
      if (editing) {
        await updateAppointmentTypeCriteria({
          id: editing,
          ...payload,
        }).unwrap();
      } else {
        await createAppointmentTypeCriteria(payload).unwrap();
      }
      await loadCriteria();
      closeModal();
    } catch (err) {
      setError(
        err?.data?.message ||
          (editing
            ? "Failed to update appointment type criteria."
            : "Failed to create appointment type criteria.")
      );
    }
  };
  return (
    <div
      className={`relative flex flex-col w-full ${
        isWelcomePage ? "":"overflow-hidden"}`}
    >
      <div className="flex flex-col gap-3 px-4 py-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="flex flex-wrap gap-3 items-center">
            <p className="m-0 text-2xl font-semibold text-gray-900">
              Appointment Type Criteria
            </p>
            <p className="m-0 text-sm text-gray-500">
              {filteredCriteria.length} Criterias
            </p>
          </div>
          {user?.permissions?.includes('appointment_type_criteria.create_appointment_type_criteria') && (
            <button
              onClick={openAdd}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus className="mr-2 w-4 h-4" />
              Add New
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3">
          {user?.permissions?.includes('appointment_type_criteria.search') && (
            <div className="relative w-full sm:w-80">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by Payer, LOB, Program, State, Visit Types"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pr-3 pl-10 w-full h-10 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {user?.permissions?.includes('appointment_type_criteria.filter_state') && (
              <FilterMultiSelect
                label="States"
                options={states}
                value={selectedStates}
                onChange={setSelectedStates}
              />
            )}
            {user?.permissions?.includes('appointment_type_criteria.filter_payer') && (
              <FilterMultiSelect
                label="Payers"
                options={payers}
                value={selectedPayers}
                onChange={setSelectedPayers}
              />
            )}
            {user?.permissions?.includes('appointment_type_criteria.filter_lob') && (
              <FilterMultiSelect
                label="LOBs"
                options={lobs}
                value={selectedLobs}
                onChange={setSelectedLobs}
              />
            )}
            {user?.permissions?.includes('appointment_type_criteria.filter_program') && (
              <FilterMultiSelect
                label="Programs"
                options={carePrograms}
                value={selectedPrograms}
                onChange={setSelectedPrograms}
              />
            )}
            {user?.permissions?.includes('appointment_type_criteria.filter_visit_type') && (
              <FilterMultiSelect
                label="Visit Types"
                options={visitTypes}
                value={selectedVisitTypes}
                onChange={setSelectedVisitTypes}
              />
            )}
          </div>
        </div>
      </div>

      {user?.permissions?.includes('appointment_type_criteria.view_appointment_type_criteria') && (
        <div className="flex-1 px-4 pt-5 pb-6">
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-600">
              <tr>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Payer
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  LOB
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Program
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  State
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Available Visit Types
                </th>
                <th className="px-6 py-3 text-sm font-medium text-left text-white">
                  Count (# of Visit Types)
                </th>
                {user?.permissions?.includes('appointment_type_criteria.edit_appointment_type_criteria') && (
                  <th className="px-6 py-3 text-sm font-medium text-right text-white">
                    Edit
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCriteria.length === 0 ? (
                <tr>
                  <td
                    colSpan={user?.permissions?.includes('appointment_type_criteria.edit_appointment_type_criteria') ? 7 : 6}
                    className="px-6 py-8 text-sm text-center text-gray-500"
                  >
                    No criteria found.
                  </td>
                </tr>
              ) : (
                filteredCriteria.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {item.payerName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {item.lobName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {item.programName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {item.state}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {(() => {
                        const normalizedVts = normalizeVisitTypes(
                          item.visitTypes,
                          visitTypes
                        );

                        console.log(normalizedVts);
                        return (
                          <div className="flex flex-wrap gap-2">
                            {normalizedVts.slice(0, 2).map((vt) => (
                              <span
                                key={vt.id}
                                className="inline-flex items-center px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full"
                              >
                                {vt.name}
                              </span>
                            ))}
                            {normalizedVts.length > 2 && (
                              <span
                                className="inline-flex items-center px-3 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full"
                                title={normalizedVts
                                  .slice(2)
                                  .map((vt) => vt.name)
                                  .join(", ")}
                              >
                                +{normalizedVts.length - 2} more
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {normalizeVisitTypes(item.visitTypes, visitTypes).length}
                    </td>
                    {user?.permissions?.includes('appointment_type_criteria.edit_appointment_type_criteria') && (
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-blue-600 rounded-md hover:bg-blue-50"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {modalOpen && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-lg bg-white rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing
                  ? `Edit Criteria: ${formValues.payerName} / ${formValues.lobName} / ${formValues.programName} / ${formValues.state}`
                  : "Add New Criteria"}
              </h3>
              <button
                onClick={closeModal}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <form className="flex flex-col gap-4" onSubmit={handleSave}>
              <SingleSelect
                label="Payer"
                options={payers}
                value={formValues.payerId}
                onChange={(val) =>
                  setFormValues((p) => ({
                    ...p,
                    payerId: val ?? "",
                  }))
                }
              />
              <SingleSelect
                label="LOB (Line of Business)"
                options={lobs}
                value={formValues.lobId}
                onChange={(val) =>
                  setFormValues((p) => ({
                    ...p,
                    lobId: val ?? "",
                  }))
                }
              />
              <SingleSelect
                label="Program"
                options={carePrograms}
                value={formValues.programId}
                onChange={(val) =>
                  setFormValues((p) => ({
                    ...p,
                    programId: val ?? "",
                  }))
                }
              />
              <SingleSelect
                label="State"
                options={states}
                value={formValues.state}
                onChange={(val) =>
                  setFormValues((p) => ({
                    ...p,
                    state: val ?? "",
                  }))
                }
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Visit Types
                </label>
                <Select
                  isMulti
                  options={visitTypeSelectOptions}
                  value={formValues.visitTypes.map((val) => ({
                    label: val.name,
                    value: val.id,
                  }))}
                  onChange={(selected) => {
                    const sel = selected || [];
                    const hasToggle = sel.some((opt) => opt.value === "__all");
                    if (hasToggle) {
                      const allSelected =
                        formValues.visitTypes.length === visitTypes.length;
                      setFormValues((p) => ({
                        ...p,
                        visitTypes: allSelected ? [] : visitTypes,
                      }));
                    } else {
                      setFormValues((p) => ({
                        ...p,
                        visitTypes: sel
                          .filter((s) => s.value !== "__all")
                          .map((s) => ({
                            id: s.value,
                            name:
                              visitTypes.find((opt) => opt.id === s.value)
                                ?.name || s.label,
                          })),
                      }));
                    }
                  }}
                  className="text-sm"
                  classNamePrefix="select"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      maxHeight: "120px",
                      overflowY: "auto",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      maxHeight: "100px",
                      overflowY: "auto",
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }),
                  }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="top"
                  placeholder="Select visit types..."
                />
                <p className="m-0 text-xs text-gray-500">
                  Choose one or more visit types.
                </p>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-3 justify-end items-center">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || (editing ? !user?.permissions?.includes('appointment_type_criteria.edit_appointment_type_criteria') : !user?.permissions?.includes('appointment_type_criteria.create_appointment_type_criteria'))}
                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterMultiSelect = ({ label, options, value, onChange }) => {
  const selectOptions = options.map((opt) =>
    typeof opt === "string"
      ? { label: opt, value: opt }
      : { label: opt.name, value: opt.id }
  );
  const selectedValues = value.map((val) => {
    const match = selectOptions.find((opt) => opt.value === val);
    return match || { label: val, value: val };
  });

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <Select
        isMulti
        options={selectOptions}
        value={selectedValues}
        onChange={(selected) => onChange(selected.map((s) => s.value))}
        className="text-sm"
        classNamePrefix="select"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        placeholder="Select..."
      />
    </div>
  );
};

const SingleSelect = ({ label, options, value, onChange }) => {
  const selectOptions = options.map((opt) => {
    if (typeof opt === "string") {
      return { label: opt, value: opt };
    }
    const labelVal = opt?.name ?? opt?.label ?? opt?.value ?? opt?.id ?? "";
    const valueVal = opt?.id ?? opt?.value ?? labelVal;
    return { label: labelVal, value: valueVal };
  });

  const selected = value
    ? selectOptions.find((opt) => opt.value === value) || {
        label: value,
        value,
      }
    : null;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <Select
        isClearable
        options={selectOptions}
        value={selected}
        onChange={(selectedOpt) =>
          onChange(selectedOpt ? selectedOpt.value : null)
        }
        className="text-sm"
        classNamePrefix="select"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        placeholder="Select..."
      />
    </div>
  );
};

export default AppointmentTypeCriteria;
